import { useState, useEffect, useCallback } from "react";
import { useBusinessSemanticLayer } from "../../hooks/useBusinessSemanticLayer";

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * View model for Business Definition page.
 * Fetches business semantic layers for the selected DB and exposes update/delete.
 * @param {number|null} userDbId - Selected user database ID (e.g. from localStorage).
 */
export const useBusinessDefinitionViewModel = (userDbId) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    getBusinessSemanticLayers,
    addBusinessSemanticLayer,
    updateBusinessSemanticLayer,
    deleteBusinessSemanticLayer,
  } = useBusinessSemanticLayer();

  const fetchLayers = useCallback(async () => {
    if (!userDbId) {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getBusinessSemanticLayers(userDbId);
      if (result.success) {
        const raw = result.data ?? [];
        setRows(
          raw.map((row) => ({
            id: row.business_semantic_layer_id,
            concept_type: row.concept_type,
            concept_key: row.concept_key,
            concept_text: row.concept_text,
            definition: row.definition,
            created_on: formatDate(row.created_on),
            updated_on: formatDate(row.updated_on),
          }))
        );
      } else {
        setError(result.error ?? "Failed to load");
      }
    } catch (err) {
      setError(err?.message ?? "Failed to load business definitions");
    } finally {
      setLoading(false);
    }
  }, [userDbId, getBusinessSemanticLayers]);

  useEffect(() => {
    fetchLayers();
  }, [fetchLayers]);

  const addConcept = useCallback(
    async (payload) => {
      const result = await addBusinessSemanticLayer(payload);
      if (result.success) {
        await fetchLayers();
      }
      return result;
    },
    [addBusinessSemanticLayer, fetchLayers]
  );

  const updateConcept = useCallback(
    async (payload) => {
      const result = await updateBusinessSemanticLayer(payload);
      if (result.success) {
        await fetchLayers();
      }
      return result;
    },
    [updateBusinessSemanticLayer, fetchLayers]
  );

  const deleteConcept = useCallback(
    async (businessSemanticLayerId) => {
      const result = await deleteBusinessSemanticLayer(businessSemanticLayerId);
      if (result.success) {
        await fetchLayers();
      }
      return result;
    },
    [deleteBusinessSemanticLayer, fetchLayers]
  );

  return {
    rows,
    loading,
    error,
    refetch: fetchLayers,
    addConcept,
    updateConcept,
    deleteConcept,
  };
};
